import { zdrAggregateKey } from './provider-contracts';
import raw from './questionnaire.json';

/**
 * Pre-completed security-questionnaire SOT (launch-readiness B5) — one row per
 * CAIQ-Lite domain, each mapped to its VSA-Core area, answered honestly:
 * `yes` (operated today and stated on a trust page), `partial` (real controls
 * with a named gap), `planned` (a target date exists, nothing operated yet).
 *
 * Truthfulness contract: every `yes` must be backed by a claim that already
 * exists on a public trust page (security/privacy/the trust hub) —
 * this artifact summarizes the posture, it never extends it. The per-row
 * question/answer prose lives in the i18n catalogs (`questionnaire.row.<id>.*`).
 */

export type QuestionnaireAnswer = 'yes' | 'partial' | 'planned';

export interface QuestionnaireRow {
  readonly id: string;
  /** CAIQ-Lite (CSA CCM v4) domain code. */
  readonly caiq: string;
  /** VSA-Core area the row answers (verbatim across locales). */
  readonly vsa: string;
  readonly answer: QuestionnaireAnswer;
}

interface QuestionnaireDocument {
  readonly lastReviewed: string;
  readonly rows: readonly QuestionnaireRow[];
}

export const QUESTIONNAIRE: QuestionnaireDocument = raw as QuestionnaireDocument;

/** i18n keys for a row's question + answer commentary. */
export function questionKey(row: QuestionnaireRow): string {
  return `questionnaire.row.${row.id}.q`;
}
export function commentKey(row: QuestionnaireRow): string {
  // The Data Security & Privacy row's ZDR/failover answer derives from the
  // provider-contract SOT (B12) — `.signed` vs `.pending` — so the public
  // questionnaire can never claim signed ZDR contracts while they're pending.
  if (row.id === 'DSP') {
    return zdrAggregateKey('questionnaire.row.DSP.a');
  }
  return `questionnaire.row.${row.id}.a`;
}
